import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import api, { apiErr } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Search, Plus, X, MapPin, Phone, Globe, Clock, Navigation, Heart, Loader2, Upload,
  Star, Instagram, Facebook, Youtube, Linkedin, Twitter, Link2, AlertTriangle, Flag,
  Share2, Copy, MessageCircle, Mail, Tag,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p) => (p && p.startsWith("http") ? p : `${BACKEND}/api/afromap/photo/${p}`);

const _badgeCorner = (p) => {
  const base = "position:absolute;top:-5px;right:-5px;width:16px;height:16px;border-radius:50%;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;border:1.5px solid #050505;box-shadow:0 1px 3px rgba(0,0,0,.5);z-index:2;";
  if (p.partner) return `<span style="${base}background:#BF953F;color:#050505;">★</span>`;
  if (p.verified) return `<span style="${base}background:#34d399;color:#050505;">✓</span>`;
  return "";
};

const goldIcon = (badge = "") =>
  L.divIcon({
    className: "afro-pin",
    html: `<div style="position:relative;transform:translate(-50%,-100%);width:26px;height:26px;">
      <div style="width:26px;height:26px;
      background:radial-gradient(circle at 30% 30%, #F5E19B, #BF953F);
      border:2px solid #050505;border-radius:50% 50% 50% 0;rotate:45deg;
      box-shadow:0 4px 10px rgba(0,0,0,.6);"></div>${badge}</div>`,
    iconSize: [26, 26], iconAnchor: [0, 0],
  });

const photoIcon = (url, badge = "") =>
  L.divIcon({
    className: "afro-photo-pin",
    html: `<div style="position:relative;transform:translate(-50%,-100%);width:46px;height:46px;">
      <div style="width:46px;height:46px;border-radius:50% 50% 50% 4px;
      background:#050505;border:2px solid #BF953F;overflow:hidden;
      box-shadow:0 5px 14px rgba(0,0,0,.65);">
      <img src="${url}" style="width:100%;height:100%;object-fit:cover;display:block" loading="lazy" /></div>${badge}</div>`,
    iconSize: [46, 46], iconAnchor: [0, 0],
  });

const clusterIcon = (cluster) => {
  const n = cluster.getChildCount();
  const size = n < 10 ? 34 : n < 50 ? 42 : 50;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;
      justify-content:center;border-radius:50%;font-weight:700;color:#050505;
      background:radial-gradient(circle at 30% 30%, #F5E19B, #BF953F);
      border:2px solid rgba(5,5,5,.6);box-shadow:0 4px 14px rgba(191,149,63,.5);
      font-family:system-ui;font-size:13px;">${n}</div>`,
    className: "afro-cluster", iconSize: [size, size],
  });
};

const Stars = ({ value = 0, size = 14, onPick }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} onClick={onPick ? () => onPick(i) : undefined}
        className={`${onPick ? "cursor-pointer" : ""} ${i <= Math.round(value) ? "text-gold" : "text-white/20"}`}
        fill={i <= Math.round(value) ? "currentColor" : "none"} />
    ))}
  </span>
);

// Carte vivante ET visible (conteneur attaché au DOM, non masqué, dimensionné) ?
function mapReady(map) {
  if (!map || !map._loaded || !map._mapPane) return false;
  const c = map.getContainer && map.getContainer();
  return !!c && c.isConnected && c.offsetWidth > 0 && c.offsetHeight > 0;
}
// invalidateSize SANS pan (pan:true lit el._leaflet_pos → crash si carte masquée/détruite).
function invalidateMap(map) {
  if (!mapReady(map)) return;
  try { map.invalidateSize({ animate: false, pan: false }); } catch (_) {}
}

export default function AfroMap() {
  const { user } = useAuth();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [facets, setFacets] = useState({ regions: [], departments: [], cities: [] });
  const [activeCat, setActiveCat] = useState("");
  const [region, setRegion] = useState("");
  const [department, setDepartment] = useState("");
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  const [places, setPlaces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [favIds, setFavIds] = useState(new Set());
  const [viewMode, setViewMode] = useState(
    typeof window !== "undefined" && window.innerWidth < 1024 ? "list" : "map");

  const track = useCallback((id, event) => {
    api.post(`/afromap/places/${id}/track?event=${event}`).catch(() => {});
  }, []);

  const catLabel = useCallback((k) => categories.find((c) => c.key === k)?.label || k, [categories]);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: true }).setView([46.6, 2.4], 6);
    L.tileLayer("https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png", {
      maxZoom: 20, subdomains: "abc",
      attribution: '&copy; OpenStreetMap France | &copy; OpenStreetMap',
    }).addTo(map);
    const cluster = L.markerClusterGroup({ iconCreateFunction: clusterIcon, showCoverageOnHover: false });
    map.addLayer(cluster);
    mapRef.current = map; clusterRef.current = cluster;
    const t0 = setTimeout(() => invalidateMap(map), 200);
    return () => { clearTimeout(t0); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    api.get("/afromap/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    if (user) api.get("/afromap/favorites").then(({ data }) =>
      setFavIds(new Set((data.places || []).map((p) => p.place_id)))).catch(() => {});
  }, [user]);

  // facets cascade
  useEffect(() => {
    const params = { country: "FR" };
    if (activeCat) params.category = activeCat;
    if (region) params.region = region;
    if (department) params.department = department;
    api.get("/afromap/facets", { params }).then(({ data }) => setFacets(data)).catch(() => {});
  }, [activeCat, region, department]);

  const loadPlaces = useCallback(async () => {
    try {
      const params = { country: "FR" };
      if (activeCat) params.category = activeCat;
      if (region) params.region = region;
      if (department) params.department = department;
      if (city) params.city = city;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/afromap/places", { params });
      setPlaces(data.places || []);
    } catch (e) { /* silencieux */ }
  }, [activeCat, region, department, city, search]);

  useEffect(() => { loadPlaces(); }, [loadPlaces]);

  useEffect(() => {
    if (viewMode !== "map") return;
    const id = setTimeout(() => invalidateMap(mapRef.current), 120);
    return () => clearTimeout(id);
  }, [viewMode]);

  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster) return;
    cluster.clearLayers();
    const pts = [];
    places.forEach((p) => {
      if (p.lat == null || p.lng == null) return;
      const cover = p.logo || p.photos?.[0];
      const badge = _badgeCorner(p);
      const m = L.marker([p.lat, p.lng], { icon: cover ? photoIcon(photoUrl(cover), badge) : goldIcon(badge) });
      m.on("click", () => openPlace(p.place_id));
      cluster.addLayer(m); pts.push([p.lat, p.lng]);
    });
    if (pts.length && mapReady(mapRef.current)) {
      try { mapRef.current.fitBounds(pts, { padding: [60, 60], maxZoom: 13, animate: false }); } catch {}
    }
  }, [places]);

  const openPlace = async (id) => {
    try {
      const { data } = await api.get(`/afromap/places/${id}`);
      setSelected(data);
      track(id, "view");
      const u = new URL(window.location.href);
      u.searchParams.set("place", id);
      window.history.replaceState({}, "", u.pathname + u.search);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const closePlace = () => {
    setSelected(null);
    const u = new URL(window.location.href);
    u.searchParams.delete("place");
    window.history.replaceState({}, "", u.pathname + u.search);
  };

  useEffect(() => {
    const pid = new URLSearchParams(window.location.search).get("place");
    if (pid) openPlace(pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFav = async (id) => {
    if (!user) return toast.error("Connectez-vous pour ajouter aux favoris.");
    try {
      const { data } = await api.post(`/afromap/places/${id}/favorite`);
      setFavIds((prev) => { const n = new Set(prev); data.favorited ? n.add(id) : n.delete(id); return n; });
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Fix mobile : recalcule la taille de la carte quand on (ré)affiche la vue Carte,
  // sinon Leaflet reste blanc car il a été initialisé pendant que la liste le couvrait.
  useEffect(() => {
    if (viewMode !== "map") return;
    const ids = [setTimeout(() => invalidateMap(mapRef.current), 120),
                 setTimeout(() => invalidateMap(mapRef.current), 400)];
    return () => ids.forEach(clearTimeout);
  }, [viewMode]);

  const selCls = "bg-[#0c0c0c] border border-white/15 rounded-full text-xs px-3 py-1.5 text-white/80 outline-none focus:border-gold/40";

  return (
    <div className="relative h-[calc(100vh-3.5rem)] lg:h-screen w-full overflow-hidden bg-[#050505]" data-testid="afro-map-page">
      <div ref={mapEl} className="afro-map absolute inset-0 z-0" data-testid="afro-map-canvas" />

      {/* Bascule Carte / Liste */}
      <div className="absolute z-[500] bottom-6 left-4 lg:left-6 pointer-events-auto flex rounded-full border border-gold/30 overflow-hidden"
        style={{ background: "rgba(8,8,8,.85)", backdropFilter: "blur(12px)" }}>
        <button data-testid="afro-view-map" onClick={() => setViewMode("map")}
          className={`text-xs px-4 py-2 font-medium transition-colors ${viewMode === "map" ? "gold-gradient text-black" : "text-white/60 hover:text-white"}`}>Carte</button>
        <button data-testid="afro-view-list" onClick={() => setViewMode("list")}
          className={`text-xs px-4 py-2 font-medium transition-colors ${viewMode === "list" ? "gold-gradient text-black" : "text-white/60 hover:text-white"}`}>Liste</button>
      </div>

      {viewMode === "list" && (
        <div className="absolute inset-0 z-[400] overflow-y-auto pt-[168px] pb-28 px-3 bg-[#050505]" data-testid="afro-list-view">
          <div className="max-w-3xl mx-auto space-y-3">
            <p className="text-white/40 text-xs px-1">{places.length} lieu{places.length > 1 ? "x" : ""}</p>
            {places.length === 0 && <p className="text-white/40 text-sm text-center py-10">Aucun lieu ne correspond à votre recherche.</p>}
            {places.map((p) => (
              <ListCard key={p.place_id} p={p} catLabel={catLabel} onOpen={() => openPlace(p.place_id)} track={track} />
            ))}
          </div>
        </div>
      )}

      <div className="absolute top-0 inset-x-0 z-[500] p-3 pointer-events-none">
        <div className="max-w-4xl mx-auto space-y-2">
          <div className="pointer-events-auto flex items-center gap-2 rounded-2xl px-3 py-2 border border-gold/30"
            style={{ background: "rgba(8,8,8,.82)", backdropFilter: "blur(12px)" }}>
            <MapPin size={18} className="text-gold shrink-0" />
            <span className="font-display text-sm tracking-tight whitespace-nowrap">Carte <span className="gold-text">Afro</span></span>
            <div className="flex items-center gap-2 flex-1 min-w-0 border-l border-white/10 pl-2 ml-1">
              <Search size={15} className="text-white/40 shrink-0" />
              <input data-testid="afro-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un lieu, une ville…"
                className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-white/30" />
            </div>
          </div>

          <div className="pointer-events-auto flex gap-1.5 overflow-x-auto pb-1 no-scrollbar lg:flex-wrap">
            <Chip active={!activeCat} onClick={() => setActiveCat("")} label="Tout" />
            {categories.map((c) => (
              <Chip key={c.key} active={activeCat === c.key} onClick={() => setActiveCat(c.key)} label={c.label} />
            ))}
          </div>

          <div className="pointer-events-auto flex gap-2 overflow-x-auto no-scrollbar">
            <select data-testid="afro-filter-region" value={region} className={selCls}
              onChange={(e) => { setRegion(e.target.value); setDepartment(""); setCity(""); }}>
              <option value="">Toutes les régions</option>
              {facets.regions.map((r) => <option key={r.value} value={r.value}>{r.value} ({r.count})</option>)}
            </select>
            <select data-testid="afro-filter-department" value={department} className={selCls}
              onChange={(e) => { setDepartment(e.target.value); setCity(""); }}>
              <option value="">Tous les départements</option>
              {facets.departments.map((d) => <option key={d.value} value={d.value}>{d.value} – {d.label} ({d.count})</option>)}
            </select>
            <select data-testid="afro-filter-city" value={city} className={selCls} onChange={(e) => setCity(e.target.value)}>
              <option value="">Toutes les villes</option>
              {facets.cities.map((c) => <option key={c.value} value={c.value}>{c.value} ({c.count})</option>)}
            </select>
            {(region || department || city || activeCat) && (
              <button data-testid="afro-filter-clear" onClick={() => { setRegion(""); setDepartment(""); setCity(""); setActiveCat(""); }}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/50 hover:text-white whitespace-nowrap">Réinitialiser</button>
            )}
          </div>
        </div>
      </div>

      <button data-testid="afro-propose-btn" onClick={() => (user ? setShowSubmit(true) : toast.error("Connectez-vous pour proposer un lieu."))}
        className="absolute z-[500] bottom-6 right-4 lg:right-6 inline-flex items-center gap-2 rounded-full gold-gradient text-black px-4 py-3 font-medium shadow-lg hover:opacity-90 transition-opacity">
        <Plus size={18} /> Proposer un lieu
      </button>

      {selected && (
        <PlaceDetail place={selected} user={user} onClose={closePlace} catLabel={catLabel}
          track={track} fav={favIds.has(selected.place_id)} onFav={() => toggleFav(selected.place_id)} />
      )}
      {showSubmit && (
        <SubmitModal categories={categories} onClose={() => setShowSubmit(false)}
          onDone={() => { setShowSubmit(false); loadPlaces(); }} />
      )}
    </div>
  );
}

const Chip = ({ active, onClick, label }) => (
  <button onClick={onClick} data-testid={`afro-cat-${label}`}
    className={`whitespace-nowrap text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active ? "gold-gradient text-black border-transparent" : "text-white/70 border-white/15 hover:text-white"
    }`} style={active ? {} : { background: "rgba(8,8,8,.7)", backdropFilter: "blur(8px)" }}>
    {label}
  </button>
);

const ListCard = ({ p, catLabel, onOpen, track }) => {
  const cover = p.photos?.[0] || p.logo;
  const gps = `https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`;
  const socials = Object.entries(p.socials || {}).filter(([, v]) => v);
  const stop = (e) => e.stopPropagation();
  const btn = "inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-white/70 hover:text-gold hover:border-gold/40 transition-colors";
  return (
    <div onClick={onOpen} data-testid={`afro-list-card-${p.place_id}`}
      className="card-surface p-3 flex gap-3 cursor-pointer hover:border-gold/30 transition-colors">
      {cover ? (
        <img src={photoUrl(cover)} alt="" className="h-20 w-20 rounded-xl object-cover shrink-0" loading="lazy" />
      ) : (
        <div className="h-20 w-20 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center text-gold/40 shrink-0"><MapPin size={22} /></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium truncate">{p.name}</span>
          {p.verified && <span className="text-[10px] text-emerald-300 border border-emerald-400/30 rounded-full px-1.5">✓ Vérifié</span>}
          {p.partner && <span className="text-[10px] text-gold border border-gold/40 rounded-full px-1.5">★ Partenaire</span>}
        </div>
        <p className="text-[11px] uppercase tracking-wide text-gold/80 mt-0.5">{catLabel(p.category)}</p>
        <p className="text-white/45 text-xs mt-0.5 truncate">{[p.city, p.department].filter(Boolean).join(" · ") || "—"}</p>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {p.phone && <a href={`tel:${p.phone}`} onClick={(e) => { stop(e); track(p.place_id, "call"); }} data-testid={`afro-list-call-${p.place_id}`} className={btn}><Phone size={13} /> Appeler</a>}
          {p.website && <a href={p.website} target="_blank" rel="noreferrer" onClick={(e) => { stop(e); track(p.place_id, "website"); }} data-testid={`afro-list-web-${p.place_id}`} className={btn}><Globe size={13} /> Site</a>}
          {socials.map(([k, v]) => { const Ic = SOCIAL_ICONS[k] || Link2; return <a key={k} href={v} target="_blank" rel="noreferrer" onClick={(e) => { stop(e); track(p.place_id, "click"); }} className={btn}><Ic size={13} /></a>; })}
          {p.lat != null && <a href={gps} target="_blank" rel="noreferrer" onClick={(e) => { stop(e); track(p.place_id, "route"); }} data-testid={`afro-list-route-${p.place_id}`} className={`${btn} text-gold border-gold/40`}><Navigation size={13} /> Itinéraire</a>}
        </div>
      </div>
    </div>
  );
};

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin, twitter: Twitter, tiktok: Link2 };
const SOCIAL_LABELS = { instagram: "Instagram", facebook: "Facebook", youtube: "YouTube", linkedin: "LinkedIn", twitter: "X (Twitter)", tiktok: "TikTok" };

function PlaceDetail({ place, user, onClose, catLabel, track, fav, onFav }) {
  const gps = `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}`;
  const shareUrl = `${window.location.origin}/carte-afro?place=${place.place_id}`;
  const shareText = `${place.name} — à découvrir sur la Carte Afro de BIZARRE AI`;
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(place.rating);
  const [count, setCount] = useState(place.rating_count || 0);
  const [minRev, setMinRev] = useState(3);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [lightbox, setLightbox] = useState(-1);
  const allPhotos = place.photos || [];

  const loadReviews = useCallback(async () => {
    try {
      const { data } = await api.get(`/afromap/places/${place.place_id}/reviews`);
      setReviews(data.reviews || []); setRating(data.rating); setCount(data.rating_count); setMinRev(data.min_reviews);
    } catch {}
  }, [place.place_id]);
  useEffect(() => { loadReviews(); }, [loadReviews]);

  const submitReview = async () => {
    if (!myRating) return toast.error("Choisissez une note.");
    setBusy(true);
    try {
      await api.post(`/afromap/places/${place.place_id}/reviews`, { rating: myRating, comment: myComment });
      toast.success("Merci pour votre avis !"); setMyComment(""); setMyRating(0); loadReviews();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const report = async (id) => {
    const reason = window.prompt("Signaler cet avis — motif (optionnel) :") ?? "";
    try { await api.post(`/afromap/reviews/${id}/report`, { reason }); toast.success("Avis signalé. Merci."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reportPhoto = async (photo) => {
    const reason = window.prompt("Signaler cette image — motif (optionnel) :") ?? "";
    try { await api.post(`/afromap/places/${place.place_id}/report-photo`, { photo, reason }); toast.success("Image signalée. Merci."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Lien copié !"); }
    catch { toast.error("Impossible de copier le lien."); }
    setShowShare(false);
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: place.name, text: shareText, url: shareUrl }); setShowShare(false); return; }
      catch { /* annulé */ }
    }
    setShowShare(true);
  };
  const shareInstagram = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch {}
    toast.success("Lien copié ! Ouvrez Instagram et collez-le dans votre story ou message.");
    setShowShare(false);
  };
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  const socials = Object.entries(place.socials || {}).filter(([, v]) => v);
  const heroSrc = place.photos?.[0];
  const gallery = (place.photos || []).filter((p) => p !== heroSrc);

  return (
    <div className="absolute z-[600] inset-x-0 bottom-0 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[440px]" data-testid="afro-place-detail">
      <div className="absolute inset-0 lg:hidden" onClick={onClose} />
      <div className="relative h-[82vh] lg:h-full overflow-y-auto border-t lg:border-t-0 lg:border-l border-gold/25 rounded-t-3xl lg:rounded-none"
        style={{ background: "rgba(6,6,6,.97)", backdropFilter: "blur(16px)" }}>
        <button onClick={onClose} data-testid="afro-detail-close"
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/50 border border-white/15 flex items-center justify-center text-white/70 hover:text-white"><X size={20} /></button>

        {/* Photo principale */}
        {heroSrc ? (
          <div className="relative h-52 w-full" data-testid="afro-hero-photo">
            <img src={photoUrl(heroSrc)} alt={place.name} onClick={() => setLightbox(allPhotos.indexOf(heroSrc))}
              className="h-full w-full object-cover cursor-zoom-in" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-transparent to-transparent pointer-events-none" />
            <span className="absolute bottom-3 left-4 text-[11px] uppercase tracking-widest text-gold border border-gold/40 rounded-full px-2.5 py-0.5 bg-black/50">{catLabel(place.category)}</span>
            {user && (
              <button onClick={() => reportPhoto(heroSrc)} data-testid="afro-report-photo"
                title="Signaler cette image"
                className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/50 border border-white/15 flex items-center justify-center text-white/60 hover:text-red-400"><Flag size={14} /></button>
            )}
          </div>
        ) : (
          <div className="px-5 pt-6"><span className="inline-block text-[11px] uppercase tracking-widest text-gold border border-gold/30 rounded-full px-2.5 py-0.5">{catLabel(place.category)}</span></div>
        )}

        <div className="p-5">
          {/* Galerie */}
          {gallery.length > 0 && (
            <div className="flex gap-2 overflow-x-auto -mx-1 mb-4 pb-1 no-scrollbar" data-testid="afro-gallery">
              {gallery.map((p, i) => (
                <img key={i} src={photoUrl(p)} alt="" onClick={() => setLightbox(allPhotos.indexOf(p))}
                  className="h-16 w-16 rounded-lg object-cover shrink-0 cursor-zoom-in border border-white/10 hover:border-gold/50 transition-colors" />
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            {rating != null ? (
              <span className="inline-flex items-center gap-1 text-xs text-gold"><Stars value={rating} /> {rating}/5 <span className="text-white/40">({count})</span></span>
            ) : count > 0 ? (
              <span className="text-[11px] text-white/40">Note à venir ({count}/{minRev} avis)</span>
            ) : null}
          </div>
          <div className="flex items-start gap-3 mb-2">
            {place.logo && (
              <img src={photoUrl(place.logo)} alt="logo" data-testid="afro-logo"
                className="h-12 w-12 rounded-lg object-contain bg-white/[0.06] border border-white/10 shrink-0" />
            )}
            <div className="min-w-0">
              <h2 className="font-display text-2xl tracking-tight">{place.name}</h2>
              {(place.verified || place.partner) && (
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {place.verified && <span data-testid="afro-badge-verified" className="text-[11px] text-emerald-300 border border-emerald-400/30 rounded-full px-2 py-0.5">✓ Vérifié</span>}
                  {place.partner && <span data-testid="afro-badge-partner" className="text-[11px] text-gold border border-gold/40 rounded-full px-2 py-0.5">★ Partenaire</span>}
                </div>
              )}
            </div>
          </div>
          {place.description && <p className="text-white/65 text-sm leading-relaxed mb-4">{place.description}</p>}

          {/* Coordonnées */}
          <div className="space-y-2.5 text-sm mb-4">
            {(place.address || place.city) && <Row icon={MapPin}>{[place.address, place.postal_code, place.city].filter(Boolean).join(", ")}{place.department ? ` — ${place.department}` : ""}</Row>}
            {place.hours && <Row icon={Clock}>{place.hours}</Row>}
          </div>

          {/* Tags / mots-clés */}
          {place.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="afro-tags">
              {place.tags.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[11px] text-white/65 border border-white/12 rounded-full px-2 py-0.5">
                  <Tag size={10} className="text-gold/60" /> {t}
                </span>
              ))}
            </div>
          )}

          {/* Boutons d'accès direct (site, appel, e-mail, réseaux) */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="afro-quick-actions">
            {place.website && (
              <QuickBtn href={place.website} icon={Globe} label="Site web" testid="afro-btn-website" onClick={() => track && track(place.place_id, "website")} />
            )}
            {place.phone && (
              <QuickBtn href={`tel:${place.phone}`} icon={Phone} label="Appeler" testid="afro-btn-call" onClick={() => track && track(place.place_id, "call")} />
            )}
            {place.email && (
              <QuickBtn href={`mailto:${place.email}`} icon={Mail} label="E-mail" testid="afro-btn-email" onClick={() => track && track(place.place_id, "click")} />
            )}
            {socials.map(([k, v]) => (
              <QuickBtn key={k} href={v} icon={SOCIAL_ICONS[k] || Link2} label={SOCIAL_LABELS[k] || k} testid={`afro-btn-${k}`} onClick={() => track && track(place.place_id, "click")} />
            ))}
          </div>

          {/* Actions principales */}
          <div className="flex gap-2">
            {place.lat != null && (
              <a href={gps} target="_blank" rel="noreferrer" data-testid="afro-gps-link"
                onClick={() => track && track(place.place_id, "route")}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full gold-gradient text-black py-2.5 text-sm font-medium"><Navigation size={16} /> Itinéraire</a>
            )}
            <button onClick={onFav} data-testid="afro-fav-btn"
              className={`inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${fav ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/70 hover:text-white"}`}>
              <Heart size={16} fill={fav ? "currentColor" : "none"} /> {fav ? "Favori" : "Favoris"}
            </button>
            <button onClick={nativeShare} data-testid="afro-share-btn" title="Partager"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 text-white/70 hover:text-gold hover:border-gold/40 px-4 py-2.5 text-sm transition-colors">
              <Share2 size={16} /> Partager
            </button>
          </div>

          {/* Menu de partage (fallback desktop / options explicites) */}
          {showShare && (
            <div className="mt-3 grid grid-cols-2 gap-2 p-3 rounded-2xl border border-white/10 bg-white/[0.03]" data-testid="afro-share-menu">
              <a href={waHref} target="_blank" rel="noreferrer" onClick={() => setShowShare(false)} data-testid="afro-share-whatsapp"
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-sm text-white/80 hover:text-gold hover:border-gold/40 transition-colors"><MessageCircle size={16} /> WhatsApp</a>
              <a href={fbHref} target="_blank" rel="noreferrer" onClick={() => setShowShare(false)} data-testid="afro-share-facebook"
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-sm text-white/80 hover:text-gold hover:border-gold/40 transition-colors"><Facebook size={16} /> Facebook</a>
              <button onClick={shareInstagram} data-testid="afro-share-instagram"
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-sm text-white/80 hover:text-gold hover:border-gold/40 transition-colors"><Instagram size={16} /> Instagram</button>
              <button onClick={copyLink} data-testid="afro-share-copy"
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-sm text-white/80 hover:text-gold hover:border-gold/40 transition-colors"><Copy size={16} /> Copier le lien</button>
            </div>
          )}

          {/* Avis communautaires */}
          <div className="mt-6 pt-5 border-t border-white/10" data-testid="afro-reviews">
            <h3 className="font-display text-lg mb-3">Avis de la communauté</h3>
            {user && (
              <div className="card-surface p-3 mb-4">
                <div className="flex items-center gap-2 mb-2"><span className="text-white/50 text-xs">Votre note :</span><Stars value={myRating} size={18} onPick={setMyRating} /></div>
                <textarea data-testid="afro-review-comment" value={myComment} onChange={(e) => setMyComment(e.target.value)} rows={2}
                  placeholder="Partagez votre expérience…" className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-gold/40" />
                <div className="mt-2"><MicButton testid="afro-review-mic" value={myComment} onChange={setMyComment} /></div>
                <button onClick={submitReview} disabled={busy} data-testid="afro-review-submit"
                  className="mt-2 inline-flex items-center gap-2 gold-gradient text-black rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Star size={15} />} Publier mon avis
                </button>
              </div>
            )}
            {reviews.length === 0 ? (
              <p className="text-white/30 text-sm">Aucun avis pour le moment. Soyez le premier !</p>
            ) : reviews.map((r) => (
              <div key={r.review_id} className="py-3 border-b border-white/5 last:border-0" data-testid={`afro-review-${r.review_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{r.user_name}</span>
                  <div className="flex items-center gap-2"><Stars value={r.rating} size={12} />
                    {user && <button onClick={() => report(r.review_id)} data-testid={`afro-report-${r.review_id}`} className="text-white/30 hover:text-red-400" title="Signaler"><Flag size={13} /></button>}
                  </div>
                </div>
                {r.comment && <p className="text-white/60 text-sm mt-1">{r.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {lightbox >= 0 && allPhotos[lightbox] && (
        <div className="fixed inset-0 z-[900] bg-black/95 flex items-center justify-center" data-testid="afro-lightbox"
          onClick={() => setLightbox(-1)}>
          <button onClick={() => setLightbox(-1)} data-testid="afro-lightbox-close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white/80 hover:text-white"><X size={22} /></button>
          {allPhotos.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + allPhotos.length) % allPhotos.length); }}
              data-testid="afro-lightbox-prev"
              className="absolute left-3 md:left-6 w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white/80 hover:text-gold text-2xl">‹</button>
          )}
          <img src={photoUrl(allPhotos[lightbox])} alt="" onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg" />
          {allPhotos.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % allPhotos.length); }}
              data-testid="afro-lightbox-next"
              className="absolute right-3 md:right-6 w-11 h-11 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white/80 hover:text-gold text-2xl">›</button>
          )}
          {user && (
            <button onClick={(e) => { e.stopPropagation(); reportPhoto(allPhotos[lightbox]); }}
              className="absolute bottom-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 text-sm rounded-full border border-white/20 bg-black/60 px-4 py-2 text-white/70 hover:text-red-400"><Flag size={14} /> Signaler cette image</button>
          )}
        </div>
      )}
    </div>
  );
}

const QuickBtn = ({ href, icon: Icon, label, testid, onClick }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testid} onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-sm text-white/80 hover:text-gold hover:border-gold/40 transition-colors">
    <Icon size={15} /> {label}
  </a>
);

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2.5 text-white/75"><Icon size={16} className="text-gold/70 shrink-0 mt-0.5" /> <span>{children}</span></div>
);

function SubmitModal({ categories, onClose, onDone }) {
  const [form, setForm] = useState({
    name: "", category: "restaurant", description: "", address: "", city: "", postal_code: "",
    country: "FR", phone: "", website: "", hours: "",
    socials: { instagram: "", facebook: "", tiktok: "" },
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dups, setDups] = useState([]);

  const setSocial = (k) => (e) => setForm({ ...form, socials: { ...form.socials, [k]: e.target.value } });

  const checkDup = async () => {
    if (!form.name.trim()) return;
    try {
      const { data } = await api.get("/afromap/check-duplicate", { params: { name: form.name, city: form.city } });
      setDups(data.duplicates || []);
    } catch {}
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/afromap/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, data.path]);
      }
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Indiquez le nom du lieu.");
    setBusy(true);
    try {
      const socials = Object.fromEntries(Object.entries(form.socials).filter(([, v]) => v));
      const { data } = await api.post("/afromap/places", { ...form, socials, photos });
      toast.success(data.message || "Proposition envoyée pour validation.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[700] flex items-end lg:items-center justify-center p-0 lg:p-6" data-testid="afro-submit-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full lg:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl lg:rounded-3xl border border-gold/25 p-6" style={{ background: "#0A0A0A" }}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="afro-submit-close"><X size={22} /></button>
        <h2 className="font-display text-xl mb-1">Proposer un lieu</h2>
        <p className="text-white/45 text-sm mb-5">Votre proposition sera publiée après validation par notre équipe.</p>
        {dups.length > 0 && (
          <div className="mb-4 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-200" data-testid="afro-dup-warning">
            <p className="flex items-center gap-2 font-medium"><AlertTriangle size={15} /> Ce lieu existe peut-être déjà :</p>
            <ul className="mt-1 list-disc list-inside text-yellow-100/80">
              {dups.map((d) => <li key={d.place_id}>{d.name}{d.city ? ` — ${d.city}` : ""} <span className="text-yellow-100/50">({d.status})</span></li>)}
            </ul>
          </div>
        )}
        <div className="space-y-3">
          <Field label="Nom du lieu *"><input data-testid="afro-f-name" value={form.name} onBlur={checkDup} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
          <Field label="Catégorie">
            <select data-testid="afro-f-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
              {categories.map((c) => <option key={c.key} value={c.key} className="bg-[#0A0A0A]">{c.label}</option>)}
            </select>
          </Field>
          <Field label="Description"><textarea data-testid="afro-f-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /><div className="mt-2"><MicButton testid="afro-f-desc-mic" value={form.description} onChange={(v) => setForm({ ...form, description: v })} /></div></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Adresse"><input data-testid="afro-f-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></Field>
            <Field label="Ville"><input data-testid="afro-f-city" value={form.city} onBlur={checkDup} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></Field>
            <Field label="Code postal"><input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className={inputCls} /></Field>
            <Field label="Téléphone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Site internet"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} placeholder="https://" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Instagram"><input value={form.socials.instagram} onChange={setSocial("instagram")} className={inputCls} placeholder="https://" /></Field>
            <Field label="Facebook"><input value={form.socials.facebook} onChange={setSocial("facebook")} className={inputCls} placeholder="https://" /></Field>
            <Field label="TikTok"><input value={form.socials.tiktok} onChange={setSocial("tiktok")} className={inputCls} placeholder="https://" /></Field>
          </div>
          <Field label="Horaires"><input value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className={inputCls} placeholder="Lun-Sam 10h-19h" /></Field>
          <Field label="Photos">
            <label className="flex items-center gap-2 cursor-pointer rounded-xl border border-dashed border-white/20 px-3 py-2.5 text-sm text-white/60 hover:text-white">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? "Envoi…" : "Ajouter des photos"}
              <input data-testid="afro-f-photos" type="file" accept="image/*" multiple className="hidden" onChange={upload} />
            </label>
            <p className="text-white/30 text-[11px] mt-1">5 photos recommandées pour une fiche attrayante.</p>
            {photos.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {photos.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={photoUrl(p)} alt="" className="h-14 w-14 rounded-lg object-cover" />
                    <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, x) => x !== i))}
                      data-testid={`afro-f-photo-del-${i}`}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black border border-white/20 text-red-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>
        <button onClick={submit} disabled={busy} data-testid="afro-submit-btn"
          className="w-full mt-5 inline-flex items-center justify-center gap-2 gold-gradient text-black rounded-full py-3 font-medium disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />} Envoyer pour validation
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-gold/40";
const Field = ({ label, children }) => (
  <label className="block"><span className="text-white/50 text-xs mb-1 block">{label}</span>{children}</label>
);
